/**
 * Paths that require an authenticated session (ProtectedLayout children).
 */
const PROTECTED_ROUTE_PREFIXES = [
  "/dashboard",
  "/settings",
  "/profile",
  "/assets",
  "/transactions",
  "/goals",
  "/admin",
  "/reports",
  "/expenses",
  "/account",
  "/investments",
  "/family",
  "/sheets",
  "/preference",
  "/system",
  "/splitmage",
  "/referrals/dashboard",
  "/subscription",
  "/create-company",
  "/c/",
] as const;

const BLOG_PUBLIC_STATIC_PATHS = new Set(["/blog", "/blog/search"]);

const BLOG_AUTH_REQUIRED_PREFIXES = [
  "/blog/my-submissions",
  "/blog/moderation",
  "/blog/collections",
  "/blog/leaderboard",
  "/blog/new/edit",
] as const;

const PUBLIC_MARKETING_STATIC_PATHS = new Set([
  "/",
  "/about",
  "/pricing",
  "/contact",
  "/referrals",
  "/faq",
  "/features",
  "/terms",
  "/privacy",
  "/contact-us",
]);

/** Blog sub-routes that require login (distinct from public article reading). */
export function isBlogAuthRequiredPath(pathname: string): boolean {
  if (
    BLOG_AUTH_REQUIRED_PREFIXES.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
    )
  ) {
    return true;
  }
  if (pathname.startsWith("/blog/submission/") && pathname.endsWith("/edit")) {
    return true;
  }
  if (/^\/blog\/[^/]+\/edit$/.test(pathname)) {
    return true;
  }
  return false;
}

/** Public blog paths (marketing list, search, article reading) — no login required. */
export function isPublicBlogPath(pathname: string): boolean {
  if (!pathname.startsWith("/blog")) {
    return false;
  }
  if (BLOG_PUBLIC_STATIC_PATHS.has(pathname)) {
    return true;
  }
  if (isBlogAuthRequiredPath(pathname)) {
    return false;
  }
  return /^\/blog\/[^/]+$/.test(pathname);
}

export function isProtectedPath(pathname: string): boolean {
  if (isBlogAuthRequiredPath(pathname)) {
    return true;
  }
  return PROTECTED_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/** Auth sign-in routes (OAuth callback landing). */
export function isAuthRoutePath(pathname: string): boolean {
  return pathname === "/auth" || pathname.startsWith("/auth?");
}

/** Public marketing routes where unauthenticated users may browse. */
export function isPublicMarketingRoute(pathname: string): boolean {
  if (PUBLIC_MARKETING_STATIC_PATHS.has(pathname)) {
    return true;
  }
  if (pathname.startsWith("/r/")) {
    return true;
  }
  return isPublicBlogPath(pathname);
}
